// src/views/student/StudentRegister.js
// Wersja w stylu "glass + campus bg" — spójna z logowaniem.
// Zachowuje całą dotychczasową logikę (kamera, TFJS fallback, upload).

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';

import '../../styles/auth.css';              // użyj tego samego arkusza co login
import CAMPUS_IMG from '../../assets/kampus_A.png';
import ikona from '../../assets/icon.png';

export default function StudentRegisterCamera() {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // ← lustrzane TYLKO podgląd (foto zapisuje się normalnie)
    const MIRRORED_PREVIEW = true;

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName]   = useState('');
    const [indeks, setIndeks]       = useState('');
    const [email, setEmail]         = useState('');
    const [password, setPassword]   = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rodoConsent, setRodoConsent] = useState(false);


    const [streamErr, setStreamErr] = useState('');
    const [snapshot, setSnapshot]   = useState(null); // { blob, url, mime, name }
    const [busy, setBusy]           = useState(false);
    const [msg, setMsg]             = useState('');

    // Wykrywanie twarzy
    const [detectorReady, setDetectorReady] = useState(false);
    const [usingNative, setUsingNative]     = useState(false);
    const [faceOk, setFaceOk]               = useState(false);
    const [faceHint, setFaceHint]           = useState('Ustaw twarz w owal i spójrz w kamerę.');

    const detectionTimerRef = useRef(null);
    const blazeModelRef     = useRef(null);
    const nativeDetectorRef = useRef(null);


    // ===== KAMERA =====
    useEffect(() => {
        let stream;
        (async () => {
            try {
                setStreamErr('');
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
                    audio: false
                });
                const v = videoRef.current;
                if (!v) return;
                v.srcObject = stream;
                await v.play().catch(() => {});
            } catch (e) {
                setStreamErr('Brak dostępu do kamery. Sprawdź uprawnienia przeglądarki i HTTPS.');
            }
        })();
        return () => stream && stream.getTracks().forEach(t => t.stop());
    }, []);

    // ===== ŁADOWANIE DETEKTORA (native → fallback TFJS BlazeFace) =====
    useEffect(() => {
        let cancelled = false;

        async function loadDetector() {
            // 1) Natywny FaceDetector
            if ('FaceDetector' in window) {
                try {
                    nativeDetectorRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
                    setUsingNative(true);
                    if (!cancelled) setDetectorReady(true);
                    return;
                } catch {
                    // fallback
                }
            }
            // 2) Fallback: BlazeFace (TFJS) z CDN
            try {
                await ensureScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.14.0/dist/tf.min.js', 'tf');
                await ensureScript('https://cdn.jsdelivr.net/npm/tensorflow-blazeface-sync@0.1.3/dist/blazeface.min.js', 'blazeface');
                // @ts-ignore
                const model = await window.blazeface.load();
                blazeModelRef.current = model;
                setUsingNative(false);
                if (!cancelled) setDetectorReady(true);
            } catch (e) {
                console.error('Błąd ładowania detektora (TFJS):', e);
                setDetectorReady(false);
            }
        }

        if (!snapshot) loadDetector();
        return () => { cancelled = true; };
    }, [snapshot]);

    // Dynamiczny loader skryptu
    function ensureScript(src, globalName) {
        return new Promise((resolve, reject) => {
            if (globalName && window[globalName]) return resolve();
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error(`Load fail: ${src}`)));
                if (existing.getAttribute('data-loaded') === '1') return resolve();
                return;
            }
            const s = document.createElement('script');
            s.src = src;
            s.async = true;
            s.onload = () => { s.setAttribute('data-loaded','1'); resolve(); };
            s.onerror = () => reject(new Error(`Load fail: ${src}`));
            document.head.appendChild(s);
        });
    }

    // ===== PĘTLA WYKRYWANIA ~130ms =====
    useEffect(() => {
        if (snapshot) { clearDetectTimer(); return; }
        if (!detectorReady) {
            setFaceOk(false);
            setFaceHint('Ładuję moduł wykrywania twarzy…');
            return;
        }

        let cancelled = false;
        const off = document.createElement('canvas');
        const octx = off.getContext('2d');

        const tick = async () => {
            if (cancelled) return;
            const v = videoRef.current;
            if (!v || v.readyState < 2) return schedule();

            try {
                const vw = v.videoWidth || 0, vh = v.videoHeight || 0;
                let faces = [];

                if (usingNative && nativeDetectorRef.current) {
                    faces = await nativeDetectorRef.current.detect(v);
                    faces = (faces || []).map(f => ({
                        x: f.boundingBox.x, y: f.boundingBox.y, width: f.boundingBox.width, height: f.boundingBox.height
                    }));
                } else if (blazeModelRef.current) {
                    // @ts-ignore
                    const preds = await blazeModelRef.current.estimateFaces(v, false);
                    faces = (preds || []).map(p => {
                        const [x1,y1] = p.topLeft; const [x2,y2] = p.bottomRight;
                        return { x:x1, y:y1, width:x2-x1, height:y2-y1 };
                    });
                }

                if (!faces || faces.length === 0) {
                    setFaceOk(false); setFaceHint('Nie widzę twarzy. Ustaw się przodem i spójrz w kamerę.');
                } else if (faces.length > 1) {
                    setFaceOk(false); setFaceHint('W kadrze jest kilka twarzy. Zostań sam/a w kadrze.');
                } else {
                    const box = faces[0];
                    const cx = box.x + box.width/2, cy = box.y + box.height/2;

                    // Owal „ellipse 40% 55% at 50% 45%”
                    const rx = vw * 0.40, ry = vh * 0.55;
                    const ox = vw * 0.50, oy = vh * 0.45;

                    const norm = Math.pow((cx-ox)/rx,2) + Math.pow((cy-oy)/ry,2);
                    const centered = norm <= 1;

                    const relH = box.height / vh;
                    const tooSmall = relH < 0.25;
                    const tooBig   = relH > 0.5;

                    // prosta heurystyka jasności
                    let brightEnough = true;
                    try {
                        const sampleW = 160, sampleH = 160;
                        off.width = sampleW; off.height = sampleH;
                        octx.drawImage(v, ox - sampleW/2, oy - sampleH/2, sampleW, sampleH, 0, 0, sampleW, sampleH);
                        const data = octx.getImageData(0,0,sampleW,sampleH).data;
                        let sum = 0;
                        for (let i=0;i<data.length;i+=4){
                            sum += 0.2126*data[i] + 0.7152*data[i+1] + 0.0722*data[i+2];
                        }
                        const avg = sum / (sampleW*sampleH);
                        brightEnough = avg >= 55;
                    } catch { brightEnough = true; }

                    if (!centered)       { setFaceOk(false); setFaceHint('Wyśrodkuj twarz w owalu.'); }
                    else if (tooSmall)   { setFaceOk(false); setFaceHint('Przybliż twarz do kamery.'); }
                    else if (tooBig)     { setFaceOk(false); setFaceHint('Oddal się od kamery.'); }
                    else if (!brightEnough){ setFaceOk(false); setFaceHint('Lepsze oświetlenie: spójrz w stronę światła.'); }
                    else                 { setFaceOk(true);  setFaceHint('Super! Twarz wykryta. Możesz zrobić zdjęcie.'); }
                }
            } catch {
                setFaceOk(false);
            } finally { schedule(); }
        };

        const schedule = ()=>{ clearDetectTimer(); detectionTimerRef.current = setTimeout(tick, 130); };
        schedule();

        return ()=>{ cancelled=true; clearDetectTimer(); };
    }, [detectorReady, usingNative, snapshot]);

    function clearDetectTimer(){
        if (detectionTimerRef.current){
            clearTimeout(detectionTimerRef.current);
            detectionTimerRef.current = null;
        }
    }

    // ===== FOTO =====
    const takePhoto = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const targetW = 720, targetH = 960;
        canvas.width = targetW; canvas.height = targetH;
        const ctx = canvas.getContext('2d');

        const vw = video.videoWidth, vh = video.videoHeight;
        if (!vw || !vh) return;

        // cover crop
        const scale = Math.max(targetW / vw, targetH / vh);
        const drawW = vw * scale, drawH = vh * scale;
        const dx = (targetW - drawW) / 2, dy = (targetH - drawH) / 2;

        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, targetW, targetH);
        // UWAGA: zapis nie jest lustrzany (CSS transform dotyczy TYLKO podglądu)
        ctx.drawImage(video, dx, dy, drawW, drawH);

        canvas.toBlob((blob) => {
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            setSnapshot({ blob, url, mime: 'image/jpeg', name: 'selfie.jpg' });
            clearDetectTimer();
        }, 'image/jpeg', 0.9);
    };

    // ===== UPLOAD & SUBMIT =====
    const uploadSelfieViaBackend = async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch('/api/auth/register/photo/upload', {
            method: 'POST',
            body: fd
        });
        if (!res.ok) {
            const j = await res.json().catch(() => null);
            throw new Error(j?.blad || 'Upload selfie nieudany');
        }
        const j = await res.json();
        if (!j?.key) throw new Error('Upload selfie nie zwrócił klucza pliku');
        return j.key;
    };

    const uploadSelfie = async (file) => {
        return uploadSelfieViaBackend(file);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setMsg('');
        if (!rodoConsent) {
            setMsg('Aby się zarejestrować, musisz wyrazić zgodę na przetwarzanie danych osobowych.');
            return;
        }
        setBusy(true);
        try {
            if (!snapshot?.blob) throw new Error('Najpierw zrób zdjęcie.');

            const key = await uploadSelfie(new File([snapshot.blob], snapshot.name, { type: snapshot.mime }));

            const reg = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type':'application/json' },
                body: JSON.stringify({
                    first_name: firstName,
                    last_name:  lastName,
                    indeks,
                    email,
                    password,
                    photo_key: key
                })
            });
            if (!reg.ok) {
                const j = await reg.json().catch(()=>null);
                throw new Error(j?.blad || 'Rejestracja nieudana');
            }
            navigate('/?rejestracja=true');
        } catch (err) {
            setMsg(err.message || 'Błąd');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="auth-hero-bg"
            style={{ backgroundImage: `url("${CAMPUS_IMG}")` }}
            role="img"
            aria-label="Zdjęcie kampusu uczelni w tle"
        >
            <div className="auth-glass">
                {/* Nagłówek karty */}
                <div className="auth-head" style={{ marginBottom: 12 }}>
                    <div className="auth-badge" aria-hidden >
                        <img src={ikona} alt="Logo" style={{ height: '80px', width: '64px' }} />
                    </div>
                    <div>
                        <h1 className="auth-title">Rejestracja studenta</h1>
                        <div className="auth-sub">Wypełnij dane i wykonaj selfie do weryfikacji</div>
                    </div>
                </div>

                {/* Dwie kolumny (na mobile zwinie się w jedną) */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 16
                    }}
                >
                    {/* Formularz danych */}
                    <form id="student-register-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 10 }}>
                        <div className="auth-field">
                            <label className="auth-label" htmlFor="first">Imię</label>
                            <input id="first" className="auth-input" value={firstName} onChange={e=>setFirstName(e.target.value)} required />
                        </div>

                        <div className="auth-field">
                            <label className="auth-label" htmlFor="last">Nazwisko</label>
                            <input id="last" className="auth-input" value={lastName} onChange={e=>setLastName(e.target.value)} required />
                        </div>

                        <div className="auth-field">
                            <label className="auth-label" htmlFor="indeks">Numer indeksu</label>
                            <input id="indeks" className="auth-input" value={indeks} onChange={e=>setIndeks(e.target.value)} required />
                        </div>

                        <div className="auth-field">
                            <label className="auth-label" htmlFor="email">E-mail</label>
                            <input id="email" type="email" className="auth-input" value={email} onChange={e=>setEmail(e.target.value)} required />
                        </div>

                        <div className="auth-field">
                            <label className="auth-label" htmlFor="pass">Hasło</label>
                            <div className="auth-input-wrap">
                                <input
                                    id="pass"
                                    type={showPassword ? 'text' : 'password'}
                                    className="auth-input"
                                    value={password}
                                    onChange={e=>setPassword(e.target.value)}
                                    autoComplete="new-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="auth-eye"
                                    onClick={() => setShowPassword(v => !v)}
                                    aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                                    title={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                                >
                                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div className="auth-field" style={{ marginTop: 4 }}>
                            <label className="auth-remember" style={{ alignItems: 'flex-start', gap: 10 }}>
                                <input
                                    type="checkbox"
                                    checked={rodoConsent}
                                    onChange={(e) => setRodoConsent(e.target.checked)}
                                    required
                                />
                                <span style={{ lineHeight: 1.4 }}>
                                    Wyrażam zgodę na przetwarzanie moich danych osobowych przez Gdańską Akademię Medyczną Nauk Stosowanych
                                    w celu założenia konta i obsługi procesu egzaminowania online. Zapoznałem/am się z
                                    <a className="auth-link" href="/polityka-prywatnosci" target="_blank" rel="noreferrer"> polityką prywatności</a>.
                                </span>
                            </label>
                        </div>

                        {msg && <div className="auth-err" style={{ marginTop: 4 }}>{msg}</div>}

                    </form>

                    {/* Podgląd kamery / zdjęcia */}
                    <div style={{ display:'grid', gap: 10, alignContent: 'start' }}>
                        {!snapshot && (
                            <div className="auth-ok" role="status" aria-live="polite" style={{ background: faceOk ? 'rgba(16,185,129,.18)' : 'rgba(255,184,76,.18)', borderColor: faceOk ? 'rgba(16,185,129,.35)' : 'rgba(255,184,76,.35)', color: '#fff' }}>
                                {detectorReady ? faceHint : 'Ładuję moduł wykrywania twarzy… (działa we wszystkich nowoczesnych przeglądarkach)'}
                            </div>
                        )}
                        <div style={{ position:'relative', width:'100%', aspectRatio:'3/4', background:'#000', borderRadius:16, overflow:'hidden' }}>
                            {!snapshot ? (
                                <>
                                    <video
                                        ref={videoRef}
                                        autoPlay playsInline muted
                                        style={{
                                            width:'100%',
                                            height:'100%',
                                            objectFit:'cover',
                                            transform: MIRRORED_PREVIEW ? 'scaleX(-1)' : 'none'
                                        }}
                                    />
                                    <div
                                        style={{
                                            position:'absolute', inset:0, pointerEvents:'none',
                                            background:'radial-gradient(ellipse 40% 55% at 50% 45%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55) 61%)'
                                        }}
                                    />
                                </>
                            ) : (
                                <img src={snapshot.url} alt="Podgląd" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                            )}
                        </div>
                        {!snapshot && (
                            <button type="button" className="auth-cta" disabled={!faceOk || !detectorReady} onClick={takePhoto} style={{ marginTop: 0 }}>
                                {detectorReady ? (faceOk ? 'Zrób zdjęcie' : 'Ustaw twarz w owalu…') : 'Przygotowuję wykrywanie…'}
                            </button>
                        )}
                        <canvas ref={canvasRef} style={{ display:'none' }} />
                        {!!streamErr && <div className="auth-err">{streamErr}</div>}
                    </div>
                    <div className="student-register-submit">
                        <button type="submit" form="student-register-form" className="auth-cta" disabled={busy || !snapshot}>
                            {busy ? 'Wysyłam…' : 'Zarejestruj'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Stopka na tle */}
            <div className="auth-legal">
                © {new Date().getFullYear()} Gdańska Akademia Medyczna Nauk Stosowanych • Bezpieczne egzaminy online
            </div>

            {/* Responsywność kolumn — bez dotykania globalnego CSS */}
            <style>{`
        @media (max-width: 900px) {
          .auth-glass > div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
          }
          .student-register-submit {
            grid-column: 1 !important;
          }
        }
        @media (min-width: 901px) {
          .student-register-submit {
            grid-column: 1 / -1;
          }
          .student-register-submit .auth-cta {
            max-width: 360px;
            margin-left: auto;
          }
        }
      `}</style>
        </div>
    );
}
